import type { JSX, ReactNode } from 'react';
import type { EnvironnementDeploiement } from '@taki/ui';

/**
 * La bande STAGING : une ligne orange en haut de l'ecran, sur toute org dont
 * le document `orgs/{orgId}/settings/environnement` dit `staging` (Parametres >
 * Apps installees, owner seul). Il vivait dans `taki.config.json` jusqu'au
 * 10/10/2026.
 *
 * POURQUOI ELLE EXISTE. Le meme bundle sert la production et les staging (un
 * projet Firebase par environnement, voir l'en-tete de
 * `packages/runtime-config/src/types.ts`). Rien d'autre a l'ecran ne dit ou l'on
 * est : sans elle, on saisit une vraie donnee dans une base de test, ou une
 * donnee de test chez un client, sans le voir.
 *
 * LE DECLENCHEUR EST LA BASE DU CLIENT, JAMAIS LE BUILD. Le document est
 * lisible SANS COMPTE (comme le branding) : l'hote l'observe avant toute
 * connexion et passe sa valeur au cadre. Absent, ou `production` : rien.
 *
 * PARTOUT, ECRAN DE CONNEXION COMPRIS, ET C'EST LA DIFFERENCE AVEC LA LIGNE DE
 * VERSION DU MENU DEV. Celle-ci vit sous l'`AuthGuard` ; celle-la se lit AVANT de
 * saisir ses identifiants. D'ou `CadreEnvironnement`, monte par l'HOTE autour de
 * tout ce qu'il rend (`apps/hello/src/main.tsx`) : le splash, la connexion, le
 * refus, l'attente des droits, le shell et l'autorisation MCP passent tous
 * dessous.
 *
 * ELLE NE MASQUE RIEN. Le cadre est une COLONNE : la bande prend sa rangee
 * (`shrink-0`), le reste de l'ecran prend la hauteur qui reste. Les ecrans
 * pleine hauteur du socle (`BootScreen`, `LoginScreen`, `AccesRefuseScreen`, le
 * conteneur de `TakiShell`) se calent donc sur `h-full`, plus sur la hauteur de
 * la fenetre : avec `h-screen`, la bande les aurait pousses de 1,5 rem sous le
 * bord, dock compris. Elle cohabite avec la ligne de version : celle-ci se pose
 * dessous, dans le shell. Les VOILES plein ecran (menu Home, modales, Lia) la
 * recouvrent comme le reste : ils sont modaux, et elle reparait a leur fermeture.
 *
 * Le texte tient en un mot (regle G26 de `docs/GABARIT-ECRAN.md`). Couleur :
 * `--staging` et `--staging-ink` (`packages/ui/src/styles/tokens.css`).
 *
 * MOTION : aucune. Elle est la des le premier rendu, et n'en bouge plus.
 */

/** L'environnement demande-t-il la bande ? Absent = production. */
export function estStaging(environnement: EnvironnementDeploiement | undefined): boolean {
  return environnement === 'staging';
}

export interface StagingBandProps {
  environnement: EnvironnementDeploiement | undefined;
}

export function StagingBand({ environnement }: StagingBandProps): JSX.Element | null {
  if (!estStaging(environnement)) return null;
  return (
    <div
      role="status"
      aria-label="Environnement de staging"
      className="flex h-6 w-full shrink-0 items-center justify-center overflow-hidden px-3 text-[0.6875rem] font-semibold uppercase leading-none tracking-[0.12em]"
      style={{ background: 'var(--staging)', color: 'var(--staging-ink)' }}
    >
      Staging
    </div>
  );
}

export interface CadreEnvironnementProps {
  /** Lu par l'hote dans `settings/environnement` ; `undefined` tant qu'il n'est pas lu. */
  environnement: EnvironnementDeploiement | undefined;
  children: ReactNode;
}

/**
 * Le cadre pleine hauteur de l'hote : la bande, puis tout le reste. Il est
 * PERMANENT, staging ou non, parce que les ecrans du socle se calent sur sa
 * hauteur (`h-full`) : sans lui, ils n'en ont plus. En production il ne coute
 * qu'une `div`, et rien ne bouge a l'ecran.
 */
export function CadreEnvironnement({ environnement, children }: CadreEnvironnementProps): JSX.Element {
  return (
    <div className="flex h-screen w-full flex-col overflow-hidden">
      <StagingBand environnement={environnement} />
      {/* overflow-y-auto : un ecran qui ne tient pas (la connexion sur une
          fenetre basse) defile ICI, comme la page defilait avant le cadre. */}
      <div className="min-h-0 w-full flex-1 overflow-y-auto">{children}</div>
    </div>
  );
}
